/**
 * A state marked by a small dot and its label in body ink — for a value every
 * row has (a server online or offline), where a chip on each row would be
 * decoration (hybrid-3c §8: chips mark the exception). The dot's tone comes
 * from the theme's status roles, never a literal colour.
 */

import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import type { StatusRole } from '@/lightTheme';

export interface StatusDotProps {
  /** What the reader has to do about the state; the theme picks the colour. */
  tone: StatusRole;
  /** The state (or the thing whose state it is) in words — the dot alone never carries it. */
  label: React.ReactNode;
}

export const StatusDot: React.FC<StatusDotProps> = ({ tone, label }) => {
  const theme = useTheme();
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0, maxWidth: '100%' }}>
      <Box
        component="span"
        aria-hidden
        sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, backgroundColor: theme.custom.status[tone].dot }}
      />
      {/* One line: a hyphenated name (kyc-ocr-mad-01) broken over two reads as two things. */}
      <Typography
        component="span"
        variant="body2"
        sx={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export default StatusDot;
